import { navigate } from 'gatsby'
import React from 'react'
import {
  ExperimentsResponse,
  FilesResponse,
  GetExperientsParams,
  GetPatientsParams,
  KraneLanguage,
  KraneRole,
  Organization,
  ResponseList,
  UserModel,
} from '../utils/models'
import useFetch, { betterFetch, HookResult } from './useFetch'

interface InsertOrganizationModel {
  name: string
  contactEmail: string
  code: string
}

export interface InsertOrganizationAdmin {
  sex: 'male' | 'female'
  birthday?: Date
  email: string
  firstname: string
  lastname: string
  language?: KraneLanguage
  studies?: Array<string>
  roles?: Array<KraneRole>
  permissions?: Array<string>
  orgAdminIn: Array<string>
  productAdminIn?: Array<string>
}

export interface Paging {
  page: number
  limit: number
}

export function useFetchLoggedUser(inputs?: React.DependencyList): HookResult<UserModel> {
  return useFetch<UserModel>('/api/user', { method: 'GET' }, inputs)
}

export function useFetchOrganizations(inputs?: React.DependencyList): HookResult<ResponseList<Organization>> {
  const [loading, result, error, setData] = useFetch<ResponseList<Organization>>(
    '/api/organization',
    { method: 'GET' },
    inputs,
  )

  return [loading, result, error, setData]
}

export function useFetchOrganizationAdmins(
  paging: Paging,
  inputs?: React.DependencyList,
): HookResult<ResponseList<UserModel>> {
  const [loading, result, error, setData] = useFetch<ResponseList<UserModel>>(
    '/api/product/admins',
    { method: 'GET', params: paging },
    inputs,
  )

  return [loading, result, error, setData]
}

export async function addOrganization(data: InsertOrganizationModel): Promise<Organization> {
  const result = await betterFetch('/api/organization', { method: 'POST', data })

  return result as Organization
}

export async function addOrganizationAdmin(data: InsertOrganizationAdmin): Promise<UserModel> {
  const result = await betterFetch('/api/user', {
    method: 'POST',
    data: { ...data, roles: ['OrganizationAdmin'] } as InsertOrganizationAdmin,
  })

  return result as UserModel
}

export async function logout(): Promise<void> {
  await betterFetch('/api/logout', {
    method: 'POST',
  })
}

export async function getOrganizations(data: InsertOrganizationModel): Promise<Organization> {
  const result = await betterFetch('/api/organization', { method: 'POST', data })

  return result as Organization
}

export function useFetchUsers(
  inputs?: React.DependencyList,
  params?: GetPatientsParams,
): HookResult<ResponseList<UserModel>> {
  const queryString = new URLSearchParams()

  for (const key in params) {
    if (!params[key]) continue
    queryString.append(key, params[key])
  }

  const [loading, result, error, setData] = useFetch<ResponseList<UserModel>>(
    `/api/user/patients?${queryString.toString()}`,
    { method: 'GET' },
    inputs,
  )

  if (error && error.status === 401) {
    navigate('/')
  }

  return [loading, result, error, setData]
}

export function useFetchExperiments(
  inputs?: React.DependencyList,
  params?: GetExperientsParams,
): HookResult<{ items: ExperimentsResponse }> {
  const queryString = new URLSearchParams()

  for (const key in params) {
    if (!params[key]) continue
    queryString.append(key, params[key])
  }

  const [loading, result, error, setData] = useFetch<{ items: ExperimentsResponse }>(
    `/api/experiment/patient?${queryString.toString()}`,
    { method: 'GET' },
    inputs,
  )

  if (error && error.status === 401) {
    navigate('/')
  }

  return [loading, result, error, setData]
}

export function useFetchFiles(
  patientId: string,
  isOpen: boolean,
  inputs?: React.DependencyList,
  params?: GetExperientsParams,
): HookResult<{ items: FilesResponse[] }> {
  const queryString = new URLSearchParams()
  for (const key in params) {
    if (!params[key]) continue
    queryString.append(key, params[key])
  }

  const [loading, result, error, setData] = useFetch<{ items: FilesResponse[] }>(
    `/api/patient/${patientId}/files/?${queryString.toString()}`,
    { method: 'GET' },
    inputs,
  )

  if (error && error.status === 401) {
    navigate('/')
  }

  return [loading, result, error, setData]
}

export async function uploadFile(
  apiUri: string,
  formData: FormData,
  onProgress: (progress: number) => void,
): Promise<void> {
  try {
    await betterFetch(
      apiUri,
      {
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)

          onProgress(percentCompleted)
        },
      },
      0,
    )
  } finally {
    //
  }
}
